import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import patientService from '../../services/patientService';
import {
  extractWallClockDateYmd,
  formatMeasurementTableDate,
  checkBPValues,
  checkBGValue,
  checkWeightValue,
  getMeasurementAlertStatus
} from '../../utils/measurementUtils';
import AssignedPatientReadingsModal from './AssignedPatientReadingsModal';
import StatusBadge from '../Common/StatusBadge/StatusBadge';
import ActionButtons from '../Common/ActionButtons/ActionButtons';
import PulseIcon from '../Common/PulseIcon/PulseIcon';
import '../../pages/PracticeManagement/PatientManagement/PatientManagement.css';
import './AssignedReviewsModal.css';

export const SHOW_ASSIGNED_REVIEWS_EVENT = 'SHOW_ASSIGNED_REVIEWS_MODAL';

export const triggerShowAssignedReviews = () => {
  window.dispatchEvent(new CustomEvent(SHOW_ASSIGNED_REVIEWS_EVENT));
};

const patientGroupKey = (item) => `${item.practice_id}_${item.patient_id}`;

const readingItemKey = (item) => `${item.patient_id}_${item.measurement_id || 'patient'}_${item.vital_type || ''}`;

const groupAssignedByPatient = (list) => {
  const groups = [];
  const indexByKey = new Map();
  (list || []).forEach((item) => {
    const key = patientGroupKey(item);
    if (!indexByKey.has(key)) {
      indexByKey.set(key, groups.length);
      groups.push({
        patient_id: item.patient_id,
        practice_id: item.practice_id,
        practice_name: item.practice_name,
        first_name: item.first_name,
        last_name: item.last_name,
        account_number: item.account_number,
        readings: []
      });
    }
    groups[indexByKey.get(key)].readings.push(item);
  });
  groups.forEach((group) => {
    group.readings.sort((a, b) =>
      String(b?.reading_date || b?.assigned_at || '').localeCompare(
        String(a?.reading_date || a?.assigned_at || '')
      )
    );
  });
  return groups;
};

const classifyVitalType = (vitalType) => {
  const vt = String(vitalType || '').toLowerCase();
  if (vt.includes('glucose') || vt === 'bg' || vt === 'blood-glucose') return 'bg';
  if (vt.includes('weight') || vt === 'wt') return 'wt';
  if (vt.includes('pressure') || vt === 'bp' || vt === 'blood-pressure') return 'bp';
  return 'other';
};

const parseAssignedReadingValues = (reading) => {
  const type = classifyVitalType(reading?.vital_type);
  const parsed = { type, sys: null, dia: null, pulse: null, glucose: null, weight: null };
  const raw = String(reading?.reading_value || '');
  if (type === 'bp') {
    const match = raw.match(/([\d.]+)\s*\/\s*([\d.]+)/);
    if (match) {
      parsed.sys = Math.round(Number(match[1]));
      parsed.dia = Math.round(Number(match[2]));
    }
    const pulse = Number(reading?.pulse);
    if (Number.isFinite(pulse) && pulse > 0) parsed.pulse = Math.round(pulse);
  } else if (type === 'bg') {
    const match = raw.match(/([\d.]+)/);
    if (match) parsed.glucose = Math.round(Number(match[1]));
  } else if (type === 'wt') {
    const match = raw.match(/([\d.]+)/);
    if (match) parsed.weight = Number(match[1]);
  }
  return parsed;
};

const displayValuesForGroup = (readings, activeIndex) => {
  const merged = { sys: null, dia: null, pulse: null, glucose: null, weight: null };
  (readings || []).forEach((reading) => {
    const parsed = parseAssignedReadingValues(reading);
    if (parsed.sys != null) merged.sys = parsed.sys;
    if (parsed.dia != null) merged.dia = parsed.dia;
    if (parsed.pulse != null) merged.pulse = parsed.pulse;
    if (parsed.glucose != null) merged.glucose = parsed.glucose;
    if (parsed.weight != null) merged.weight = parsed.weight;
  });
  const active = parseAssignedReadingValues((readings || [])[activeIndex]);
  if (active.sys != null) merged.sys = active.sys;
  if (active.dia != null) merged.dia = active.dia;
  if (active.pulse != null) merged.pulse = active.pulse;
  if (active.glucose != null) merged.glucose = active.glucose;
  if (active.weight != null) merged.weight = active.weight;
  return merged;
};

const vitalTypesForGroup = (readings) => {
  const latestByLabel = {};
  (readings || []).forEach((reading) => {
    const type = classifyVitalType(reading?.vital_type);
    const label = type === 'bp' ? 'BP' : type === 'bg' ? 'BG' : type === 'wt' ? 'WT' : null;
    if (!label) return;
    const date = String(reading?.reading_date || reading?.assigned_at || '');
    if (!latestByLabel[label] || date > latestByLabel[label]) {
      latestByLabel[label] = date;
    }
  });
  return Object.keys(latestByLabel).sort((a, b) =>
    String(latestByLabel[b] || '').localeCompare(String(latestByLabel[a] || ''))
  );
};

const ASSIGNED_VITAL_TARGETS = {
  bloodPressure: {
    systolicMin: 100,
    systolicMax: 140,
    diastolicMin: 60,
    diastolicMax: 90
  },
  bloodGlucose: { minimum: 60, maximum: 110 },
  weight: { weightMin: 66, weightMax: 220 },
  pulse: { min: 60, max: 100 }
};

const vitalAlertClass = (status) => {
  if (status === 'high') return 'alert-high';
  if (status === 'low') return 'alert-low';
  if (status === 'normal') return 'alert-normal';
  return '';
};

const formatVitalCell = (value, status) => {
  if (value == null || value === '' || Number.isNaN(Number(value)) || Number(value) <= 0) {
    return <span className="arm-table-na">N/A</span>;
  }
  const cls = vitalAlertClass(status);
  return <span className={`patient-management-vital-value ${cls}`}>{value}</span>;
};

const AssignedReviewsModal = () => {
  const { isAuthenticated, user, showWelcomeMessage } = useSelector((state) => state.auth);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [assignedPatients, setAssignedPatients] = useState([]);
  const [processingKey, setProcessingKey] = useState(null);
  const [allReviewedCelebration, setAllReviewedCelebration] = useState(false);
  const [activeReadingByPatient, setActiveReadingByPatient] = useState({});
  const [viewingPatient, setViewingPatient] = useState(null);

  const fetchAssigned = useCallback(async (shouldOpen = true) => {
    if (!isAuthenticated || !user?.id || [1, 3, 6].includes(Number(user?.role_id))) return;
    try {
      setLoading(true);
      const res = await patientService.getAssignedAbnormalReviews();
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      const sorted = [...list].sort((a, b) => {
        const ta = String(a?.reading_date || a?.assigned_at || '');
        const tb = String(b?.reading_date || b?.assigned_at || '');
        return tb.localeCompare(ta);
      });
      setAssignedPatients(sorted);

      if (list.length > 0) {
        setAllReviewedCelebration(false);
        if (shouldOpen) setIsOpen(true);
      } else if (!shouldOpen) {
        setAllReviewedCelebration(true);
      }
    } catch (err) {
      console.warn('Failed to fetch assigned abnormal reviews:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return undefined;
    fetchAssigned(true);
    const retry = window.setTimeout(() => fetchAssigned(true), 1500);
    return () => window.clearTimeout(retry);
  }, [fetchAssigned, isAuthenticated, user?.id]);

  useEffect(() => {
    if (showWelcomeMessage && isAuthenticated && user?.id) {
      fetchAssigned(true);
    }
  }, [showWelcomeMessage, isAuthenticated, user?.id, fetchAssigned]);

  useEffect(() => {
    const handleCustomTrigger = () => fetchAssigned(true);
    window.addEventListener(SHOW_ASSIGNED_REVIEWS_EVENT, handleCustomTrigger);
    return () => window.removeEventListener(SHOW_ASSIGNED_REVIEWS_EVENT, handleCustomTrigger);
  }, [fetchAssigned]);

  const groupedPatients = useMemo(() => groupAssignedByPatient(assignedPatients), [assignedPatients]);

  const formatLastUpload = (val) => {
    if (!val) return 'N/A';
    return formatMeasurementTableDate(extractWallClockDateYmd(val)) || 'N/A';
  };

  const isMeasurementReading = (p) =>
    Boolean(p?.vital_type && p?.measurement_id && p.vital_type !== 'patient' && p.vital_type !== 'assigned_review');

  const reviewOneReading = async (p) => {
    if (!p.practice_id || !p.patient_id) return false;
    if (isMeasurementReading(p)) {
      const res = await patientService.reviewPatientMeasurement(
        p.practice_id,
        p.patient_id,
        p.vital_type,
        p.measurement_id
      );
      return Boolean(res?.data?.success);
    }
    const res = await patientService.reviewPatientAbnormal(p.practice_id, p.patient_id);
    return Boolean(res?.data?.success);
  };

  const handleQuickReview = async (group) => {
    const groupKey = patientGroupKey(group);
    if (!group?.practice_id || !group?.patient_id || processingKey) return;
    setProcessingKey(groupKey);
    try {
      const results = await Promise.allSettled((group.readings || []).map((reading) => reviewOneReading(reading)));
      const reviewedKeys = new Set();
      results.forEach((result, idx) => {
        if (result.status === 'fulfilled' && result.value) {
          reviewedKeys.add(readingItemKey(group.readings[idx]));
        }
      });

      if (reviewedKeys.size === 0) {
        toast.error('Failed to complete review');
        return;
      }

      toast.success(
        reviewedKeys.size === (group.readings || []).length
          ? `Review completed for ${group.first_name || ''} ${group.last_name || ''}`.trim()
          : `Reviewed ${reviewedKeys.size} of ${(group.readings || []).length} assigned readings`
      );

      const remaining = assignedPatients.filter((item) => {
        if (patientGroupKey(item) !== groupKey) return true;
        return !reviewedKeys.has(readingItemKey(item));
      });
      setAssignedPatients(remaining);

      if (remaining.length === 0) {
        setViewingPatient(null);
        setAllReviewedCelebration(true);
        setTimeout(() => {
          setIsOpen(false);
          setAllReviewedCelebration(false);
        }, 1800);
      }
    } catch (err) {
      console.error('Error reviewing assigned patient:', err);
      toast.error(err?.response?.data?.message || 'Failed to complete review');
    } finally {
      setProcessingKey(null);
    }
  };

  const handleViewPatient = (group) => {
    setViewingPatient(group);
  };

  const shiftReading = (groupKey, readingsLength, delta) => {
    if (readingsLength <= 1) return;
    setActiveReadingByPatient((prev) => {
      const current = Number(prev[groupKey] || 0);
      const next = Math.min(readingsLength - 1, Math.max(0, current + delta));
      if (next === current) return prev;
      return { ...prev, [groupKey]: next };
    });
  };

  if (typeof document === 'undefined') {
    return null;
  }

  if (!isOpen && !viewingPatient) {
    return null;
  }

  if (assignedPatients.length === 0 && !allReviewedCelebration && !viewingPatient) {
    return null;
  }

  return (
    <>
      {createPortal(
        <div
          className="arm-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="arm-modal-title"
        >
          <div className="arm-card">
            <div className="arm-header">
              <div className="arm-header-left">
                <div className="arm-header-title-row">
                  <h3 id="arm-modal-title" className="arm-header-title">
                    Assigned Abnormal Readings
                  </h3>
                </div>
                <p className="arm-header-subtitle">
                  {allReviewedCelebration
                    ? 'All assigned readings have been successfully reviewed'
                    : `You have ${assignedPatients.length} reading${assignedPatients.length === 1 ? '' : 's'} assigned across ${groupedPatients.length} patient${groupedPatients.length === 1 ? '' : 's'}`}
                </p>
              </div>
              <button
                type="button"
                className="arm-close-btn"
                onClick={() => {
                  setViewingPatient(null);
                  setIsOpen(false);
                }}
                title="Close"
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <div className="arm-body">
              {allReviewedCelebration ? (
                <div className="arm-celebration-state">
                  <h4>All Readings Reviewed</h4>
                  <p>Great job! You have cleared all abnormal readings assigned to you.</p>
                </div>
              ) : (
                <>
                  <div className="arm-banner">
                    <div className="arm-banner-text">
                      <strong>Action required:</strong> These abnormal patient readings were assigned specifically to you. Mark them reviewed here, or open the patient chart to review or reassign individual readings.
                    </div>
                  </div>

                  <div className="arm-table-wrap">
                    <table className="arm-assigned-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Status</th>
                          <th>Vitals</th>
                          <th>Name</th>
                          <th>Systolic</th>
                          <th>Diastolic</th>
                          <th>Pulse</th>
                          <th>Glucose</th>
                          <th>Weight</th>
                          <th>Last Upload</th>
                          <th>Readings</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupedPatients.map((group, idx) => {
                          const groupKey = patientGroupKey(group);
                          const readings = group.readings || [];
                          const activeIndex = Math.min(
                            Number(activeReadingByPatient[groupKey] || 0),
                            Math.max(0, readings.length - 1)
                          );
                          const readingNumber = Math.max(1, readings.length - activeIndex);
                          const activeReading = readings[activeIndex] || readings[0] || {};
                          const values = displayValuesForGroup(readings, activeIndex);
                          const bpStatus = checkBPValues(
                            { systolic_pressure: values.sys, diastolic_pressure: values.dia },
                            ASSIGNED_VITAL_TARGETS.bloodPressure
                          );
                          const pulseStatus = getMeasurementAlertStatus(
                            values.pulse,
                            ASSIGNED_VITAL_TARGETS.pulse.min,
                            ASSIGNED_VITAL_TARGETS.pulse.max
                          );
                          const glucoseStatus = checkBGValue(values.glucose, ASSIGNED_VITAL_TARGETS.bloodGlucose);
                          const weightStatus = checkWeightValue(values.weight, ASSIGNED_VITAL_TARGETS.weight);
                          const vitalLabels = vitalTypesForGroup(readings);
                          const listName =
                            `${group.last_name || ''}, ${group.first_name || ''}`.replace(/^,\s*|\s*,$/g, '').trim()
                            || `Patient #${group.patient_id}`;
                          const isProcessing = processingKey === groupKey;

                          return (
                            <tr key={groupKey}>
                              <td>{idx + 1}</td>
                              <td><StatusBadge status="A" /></td>
                              <td>
                                {vitalLabels.length > 0 ? vitalLabels.map((label) => (
                                  <span key={label} className="patient-management-vitals-badge">{label}</span>
                                )) : (
                                  <span className="patient-management-vitals-badge" style={{ opacity: 0.5 }}>-</span>
                                )}
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="patient-management-name-link arm-table-name-btn"
                                  onClick={() => handleViewPatient(group)}
                                >
                                  {listName}
                                </button>
                              </td>
                              <td>{formatVitalCell(values.sys, bpStatus.sysStatus)}</td>
                              <td>{formatVitalCell(values.dia, bpStatus.diaStatus)}</td>
                              <td>
                                {values.pulse == null ? (
                                  <span className="arm-table-na">N/A</span>
                                ) : (
                                  <span className={`patient-management-pulse-with-icon patient-management-vital-value ${vitalAlertClass(pulseStatus)}`}>
                                    {values.pulse}
                                    <PulseIcon isAbnormal={pulseStatus === 'high' || pulseStatus === 'low'} className="patient-management-pulse-icon" />
                                  </span>
                                )}
                              </td>
                              <td>{formatVitalCell(values.glucose, glucoseStatus)}</td>
                              <td>{formatVitalCell(values.weight, weightStatus)}</td>
                              <td>{formatLastUpload(activeReading.reading_date || activeReading.assigned_at)}</td>
                              <td>
                                <div className="arm-table-readings">
                                  {readings.length > 1 && (
                                    <button
                                      type="button"
                                      className="arm-switch-btn"
                                      onClick={() => shiftReading(groupKey, readings.length, 1)}
                                      disabled={activeIndex >= readings.length - 1}
                                      title="Previous assigned reading"
                                      aria-label="Previous assigned reading"
                                    >
                                      ‹
                                    </button>
                                  )}
                                  <span title={`Reading ${readingNumber} of ${readings.length}`}>
                                    {readings.length > 1 ? `${readingNumber} / ${readings.length}` : readingNumber}
                                  </span>
                                  {readings.length > 1 && (
                                    <button
                                      type="button"
                                      className="arm-switch-btn"
                                      onClick={() => shiftReading(groupKey, readings.length, -1)}
                                      disabled={activeIndex <= 0}
                                      title="Next assigned reading"
                                      aria-label="Next assigned reading"
                                    >
                                      ›
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td>
                                <ActionButtons
                                  onView={() => handleViewPatient(group)}
                                  hideEdit
                                  hideDelete
                                  extraButtons={(
                                    <button
                                      type="button"
                                      className="res-action-btn res-view-btn"
                                      onClick={() => handleQuickReview(group)}
                                      disabled={isProcessing || loading}
                                      title="Mark assigned readings as reviewed"
                                    >
                                      <FontAwesomeIcon icon={faCheck} />
                                    </button>
                                  )}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            <div className="arm-footer">
              <div className="arm-footer-info">
                {!allReviewedCelebration && (
                  <span>
                    <strong>{groupedPatients.length}</strong> patient{groupedPatients.length === 1 ? '' : 's'} ·{' '}
                    <strong>{assignedPatients.length}</strong> assigned reading{assignedPatients.length === 1 ? '' : 's'} remaining
                  </span>
                )}
              </div>
              <div className="arm-footer-actions">
                <button
                  type="button"
                  className="arm-btn-dismiss"
                  onClick={() => {
                    setViewingPatient(null);
                    setIsOpen(false);
                  }}
                >
                  Close &amp; Review Later
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {viewingPatient && (
        <AssignedPatientReadingsModal
          patientGroup={viewingPatient}
          onClose={() => setViewingPatient(null)}
          onAssignmentChanged={() => fetchAssigned(false)}
        />
      )}
    </>
  );
};

export default AssignedReviewsModal;
